import Route from '@ember/routing/route';
import { service } from '@ember/service';
import { schedule } from '@ember/runloop';
import type MetaService from 'marcemira/services/meta';
import type RouterService from '@ember/routing/router-service';
import {
  generatePersonSchema,
  generateWebsiteSchema
} from 'marcemira/utils/schema-org';

export default class IndexRoute extends Route {
  @service declare meta: MetaService;
  @service declare router: RouterService;

  activate() {
    // Trigger flip animations after render
    schedule('afterRender', () => {
      if (typeof document !== 'undefined') {
        const figure = document.querySelector('#index figure');
        const todo = document.querySelector('#todo');

        if (figure) {
          figure.classList.remove('flipped');
        }
        if (todo) {
          todo.classList.remove('flipped-2');
        }
      }
    });
  }

  afterModel() {
    const currentURL = `https://marcemira.com${this.router.currentURL}`;

    // Set basic meta data
    this.meta.title = 'Marcelo Mira - Remote Full-Stack Developer & UX/UI Designer';
    this.meta.description = "Self-taught remote full-stack developer and UX/UI designer with 26+ years of experience. Ember.js expert, TypeScript specialist, and CTO-level consultant for startups.";
    this.meta.canonicalUrl = currentURL;

    // Set alternate language URLs (hreflang)
    this.meta.alternateLanguages = [
      { lang: 'en', url: currentURL },
      { lang: 'es', url: currentURL }
    ];

    // Set additional meta tags
    this.meta.setTags([
      {
        name: 'keywords',
        content: 'Marcelo Mira, Mars, Remote Full-Stack Developer, Ember.js Expert, TypeScript Developer, Node.js, NestJS, UX/UI Designer, CTO Consultant, Agile Development, Frontend Architecture, Software Contractor, Córdoba Argentina'
      },
      { property: 'og:type', content: 'website' },
      { property: 'og:url', content: currentURL },
      { property: 'og:image', content: 'https://marcemira.com/img/marcelo_mira_3.jpg' },
      { property: 'og:image:width', content: '1920' },
      { property: 'og:image:height', content: '1080' },
      { property: 'og:image:alt', content: 'Marcelo Mira - Remote Full-Stack Developer' },
    ]);

    // Generate Person schema
    const personSchema = generatePersonSchema({
      name: 'Marcelo Ariel Mira Marin',
      jobTitle: 'Remote Full-Stack Web Developer & UX/UI Designer',
      description: "Self-taught remote full-stack developer and UX/UI designer with 26+ years of experience. Ember.js expert and TypeScript specialist, building rich user-friendly applications. CTO-level consultant for startups, specializing in frontend architecture, Node.js backends, and agile team leadership.",
      url: currentURL,
      image: 'https://marcemira.com/img/marcelo_mira_3.jpg',
      email: 'hello@marcemira.com',
      sameAs: [
        'https://www.linkedin.com/in/marcemira',
        'https://github.com/marcemira'
      ]
    });

    // Generate Website schema
    const websiteSchema = generateWebsiteSchema({
      name: 'Marcemira - Marcelo Mira Portfolio',
      url: 'https://marcemira.com',
      description: 'Personal portfolio of Marcelo Mira, a remote full-stack developer, Ember.js expert, and UX/UI designer with 26+ years of experience. CTO-level consultant specializing in frontend architecture and modern web technologies.',
      author: 'Marcelo Mira'
    });

    // Add structured data
    this.meta.scriptTags = [
      {
        type: 'application/ld+json',
        id: 'structured-data-person',
        content: JSON.stringify(personSchema)
      },
      {
        type: 'application/ld+json',
        id: 'structured-data-website',
        content: JSON.stringify(websiteSchema)
      }
    ];

    // Render meta tags to header
    this.meta.renderMetaToHeader();
  }
}
